import React, { useMemo } from 'react';
import { Box, Link, SxProps, Typography } from '@mui/material';
import { Conversation } from 'generated-graphql/types';

import * as Yup from 'yup';
import { ToastType, useToast } from 'services/toast';
import { ApplicationStage } from 'modules/conversations/types';
import { useDrawer } from 'services/drawer/hooks';
import { stageOptions } from '../constants/stageOptions';
import { Fields, Form } from 'common/form';
import { useUpdateApplicationStageMutation } from 'generated-graphql/hooks/apollo-hooks';
import { formatToUSDate } from 'utils/datetime';

const container: SxProps = {
  display: 'flex',
  flexDirection: 'column',
  minWidth: 400,
  gap: 3
};

const header: SxProps = {
  display: 'flex',
  flexDirection: 'column',
  gap: 1
};

const applicationStageSchema = Yup.object({
  applicationStage: Yup.string().required('Choose a stage'),
  stageUpdatedAt: Yup.string()
    .required('Date is required')
    .test('valid-date', 'Date must include a month, day and year.', (value) => {
      if (!value) return false;

      // Check if it matches the expected format
      const formatRegex = /^\d{4}-\d{1,2}-\d{1,2}$/;
      if (!formatRegex.test(value)) return false;

      // Try to create a Date object and check if it's valid
      const date = new Date(value);
      return !isNaN(date.getTime());
    })
});

type ApplicationStageFormData = Yup.InferType<typeof applicationStageSchema>;

const convertToAWSDateTime = (dateTime: string): string => {
  if (!dateTime) return '';
  return `${dateTime}T00:00:00.000Z`;
};

const fields: Fields<ApplicationStageFormData> = {
  applicationStage: {
    type: 'radio',
    label: 'Choose an application stage',
    options: stageOptions
  },
  stageUpdatedAt: {
    type: 'dateSlots',
    label: 'When was this change made?',
    subLabel: 'For example, Nov 10 2008'
  }
};

type Props = {
  conversations: Conversation[];
  onComplete?: () => void;
};

export const UpdateApplicationStageDrawer: React.FC<Props> = ({
  conversations,
  onComplete
}) => {
  const { showToast } = useToast();
  const { closeDrawer } = useDrawer();

  const noOfSelectedCandidates = useMemo(() => {
    return conversations.length;
  }, [conversations]);

  const [updateApplicationStageMutation, { loading }] =
    useUpdateApplicationStageMutation();

  const onSubmit = async ({
    applicationStage,
    stageUpdatedAt
  }: ApplicationStageFormData) => {
    try {
      const updateApplicationStage = applicationStage;
      const setToNull = updateApplicationStage === ApplicationStage.NO_STAGE;

      const conversationIds = conversations.map(
        (conversation) => conversation.id
      );

      await updateApplicationStageMutation({
        variables: {
          input: {
            applicationStage: setToNull ? null : updateApplicationStage,
            conversationIds,
            stageUpdatedAt: convertToAWSDateTime(stageUpdatedAt)
          }
        }
      });

      onComplete?.();
      closeDrawer();
      showToast({
        title: 'Candidate status changed',
        type: ToastType.Success
      });
    } catch (e: any) {
      showToast({
        title: 'Status could not be changed',
        message: (
          <Typography variant="body2" mt={1}>
            Try changing the status again.{' '}
            <Link href="mailto:support@joinpopp.com?subject=Failed to update conversation">
              contact support
            </Link>{' '}
            if the issue persists.
          </Typography>
        ),
        type: ToastType.Error
      });
    }
  };

  return (
    <Box sx={container}>
      <Box sx={header}>
        <Typography variant="h4" fontWeight="400">
          Change stage
        </Typography>
        <Typography variant="body1">
          {noOfSelectedCandidates} candidate(s) selected.{' '}
          {noOfSelectedCandidates === 1
            ? `Last updated ${formatToUSDate(conversations[0].updatedAt)}`
            : null}
        </Typography>
      </Box>

      <Form
        sx={{
          gap: '40px'
        }}
        schema={applicationStageSchema}
        fields={fields}
        defaultValues={{
          applicationStage:
            noOfSelectedCandidates === 1
              ? conversations[0].applicationStage ?? ApplicationStage.NO_STAGE
              : undefined,
          stageUpdatedAt: new Date().toISOString().split('T')[0]
        }}
        onSubmit={onSubmit}
        onCancel={closeDrawer}
        submitButtonProps={{
          label: 'Confirm',
          loading,
          disabled: loading
        }}
        cancelButtonProps={{
          label: 'Cancel'
        }}
      />
    </Box>
  );
};
